import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Analytics.css';

const CustomerSegmentation = () => {
  const [rfmData, setRfmData] = useState([]);
  const [segments, setSegments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyzeCustomers();
  }, []);

  const analyzeCustomers = async () => {
    try {
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      
      const orders = ordersSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
      const customers = customersSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
      
      const rfmScores = calculateRFM(customers, orders);
      setRfmData(rfmScores);
      
      const segmentation = segmentCustomers(rfmScores);
      setSegments(segmentation);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const calculateRFM = (customers, orders) => {
    const now = new Date();
    return customers.map(customer => {
      const customerOrders = orders.filter(o => o.customerPhone === customer.phone);
      const recency = customerOrders.length > 0 
        ? Math.floor((now - new Date(customerOrders[customerOrders.length - 1].timestamp?.toDate ? customerOrders[customerOrders.length - 1].timestamp.toDate() : new Date())) / (1000 * 60 * 60 * 24))
        : 999;
      const frequency = customerOrders.length;
      const monetary = customerOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      
      return {
        name: customer.name,
        phone: customer.phone,
        recency,
        frequency,
        monetary,
        recencyScore: recency < 30 ? 5 : recency < 60 ? 4 : recency < 90 ? 3 : recency < 180 ? 2 : 1,
        frequencyScore: frequency >= 10 ? 5 : frequency >= 5 ? 4 : frequency >= 3 ? 3 : frequency >= 2 ? 2 : 1,
        monetaryScore: monetary >= 5000 ? 5 : monetary >= 3000 ? 4 : monetary >= 1500 ? 3 : monetary >= 500 ? 2 : 1
      };
    });
  };

  const segmentCustomers = (rfmScores) => {
    const champions = rfmScores.filter(c => c.recencyScore >= 4 && c.frequencyScore >= 4 && c.monetaryScore >= 4);
    const loyal = rfmScores.filter(c => c.recencyScore >= 3 && c.frequencyScore >= 4);
    const atRisk = rfmScores.filter(c => c.recencyScore <= 2 && c.frequencyScore >= 3 && c.monetaryScore >= 3);
    const lost = rfmScores.filter(c => c.recencyScore === 1 && c.frequencyScore <= 2);
    
    return {
      champions: {count: champions.length, value: champions.reduce((sum, c) => sum + c.monetary, 0)},
      loyal: {count: loyal.length, value: loyal.reduce((sum, c) => sum + c.monetary, 0)},
      atRisk: {count: atRisk.length, value: atRisk.reduce((sum, c) => sum + c.monetary, 0)},
      lost: {count: lost.length, value: lost.reduce((sum, c) => sum + c.monetary, 0)}
    };
  };

  const segmentChartData = Object.keys(segments).map(key => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    customers: segments[key]?.count || 0,
    value: segments[key]?.value || 0
  }));

  const COLORS = ['#2ecc71', '#3498db', '#f39c12', '#e74c3c'];

  if (loading) return <div className="loading-container"><div className="spinner-large"></div></div>;

  return (
    <div className="customer-segmentation-page">
      <div className="page-header">
        <h1>👥 Customer Segmentation</h1>
        <p>RFM Analysis - Recency, Frequency, Monetary</p>
      </div>

      <div className="metrics-grid">
        {Object.keys(segments).map((key, idx) => (
          <div key={key} className="metric-card">
            <div className="metric-icon" style={{background: COLORS[idx]}}>{['🏆', '❤️', '⚠️', '💤'][idx]}</div>
            <div className="metric-content">
              <p className="metric-label">{key.charAt(0).toUpperCase() + key.slice(1)} Customers</p>
              <p className="metric-value">{segments[key]?.count || 0}</p>
              <p className="metric-subtitle">₹{(segments[key]?.value || 0).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-row">
        <div className="chart-container">
          <h3>Customer Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={segmentChartData} dataKey="customers" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {segmentChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Revenue by Segment</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={segmentChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
              <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
              <Bar dataKey="value" fill="#3498db" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="insights-section">
        <h3>💡 Insights & Recommendations</h3>
        <div className="insights-grid">
          <div className="insight-card"><strong>Champions ({segments.champions?.count || 0}):</strong> <p>Your best customers! Reward with exclusive perks and early access to new items.</p></div>
          <div className="insight-card"><strong>Loyal ({segments.loyal?.count || 0}):</strong> <p>Regular customers. Maintain engagement with loyalty programs and personalized offers.</p></div>
          <div className="insight-card"><strong>At Risk ({segments.atRisk?.count || 0}):</strong> <p>Haven't ordered recently. Send win-back campaigns and special discounts.</p></div>
          <div className="insight-card"><strong>Lost ({segments.lost?.count || 0}):</strong> <p>Re-engage with surveys and compelling offers to understand why they left.</p></div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSegmentation;
